/**
 * Decisions list — decision requests and their resolutions. Full CRUD resource.
 * Columns: title, decision_type, status, confidentiality, created_at.
 */
import { useMemo } from "react";
import { useNavigation } from "@refinedev/core";
import type { ColumnDef } from "@tanstack/react-table";
import type { Decision } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusBadge } from "@/components/app/StatusBadge";
import { RunsDataTable } from "../_shared/RunsDataTable";
import { formatDateTime, orDash } from "../_shared/runs-ui";

export function DecisionList() {
  const { show, create } = useNavigation();

  const columns = useMemo<ColumnDef<Decision>[]>(
    () => [
      {
        id: "title",
        accessorKey: "title",
        header: "Title",
        cell: ({ row }) => (
          <span className="font-medium">{row.original.title}</span>
        ),
      },
      {
        id: "decision_type",
        accessorKey: "decision_type",
        header: "Type",
        cell: ({ row }) => orDash(row.original.decision_type),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "confidentiality",
        accessorKey: "confidentiality",
        header: "Confidentiality",
        cell: ({ row }) => orDash(row.original.confidentiality),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Decisions"
        subtitle="Decision requests and their approvals, rejections, and rationale."
        icon="decisions"
        actions={
          <Button size="sm" onClick={() => create("decisions")}>
            <Icon name="create" size={16} className="mr-2" aria-hidden /> New
            decision
          </Button>
        }
      />
      <RunsDataTable<Decision>
        resource="decisions"
        columns={columns}
        onRowClick={(record) => show("decisions", record.id)}
        emptyMessage="No decisions recorded yet."
      />
    </div>
  );
}

export default DecisionList;
